import { json } from 'aurelia-fetch-client';
import { autoinject } from 'aurelia-framework';
import { ApiService } from '../../utils/api-service';

@autoinject
export class ImportService {
	constructor(private api: ApiService) {
	}

	previewFinancialEntryFile(files: File[]): Promise<Response> {
		const formData = new FormData();
		files.forEach(file => {
			formData.append('files', file);
		});
		return this.api.fetch(`imports/preview/financial-entries`, {
			body: formData,
			method: 'post'
		});
	}

	previewMeasureEntryFile(files: File[]): Promise<Response> {
		const formData = new FormData();
		files.forEach(file => {
			formData.append('files', file);
		});
		return this.api.fetch(`imports/preview/measure-entries`, {
			body: formData,
			method: 'post'
		});
	}

	async sendMeasureEntryFile(scopeId: string, files: File[]): Promise<Response> {
		const formData = new FormData();
		files.forEach(file => {
			formData.append('files', file);
		});
		return await this.api.fetch(`imports/send/scopes/${scopeId}/measure-entries`, {
			body: formData,
			method: 'post'
		});
	}

	async sendFinancialEntryFile(scopeId: string, files: File[], invertSigns: boolean, mode: ImportMode): Promise<Response> {
		const formData = new FormData();
		files.forEach(file => {
			formData.append('files', file);
		});
		return await this.api.fetch(`imports/send/scopes/${scopeId}/financial-entries/?invertSigns=${invertSigns}&mode=${mode}`, {
			body: formData,
			method: 'post'
		});
	}

	async setStatusForImportItem(itemId: string, status: ImportItemStatus, importType: string): Promise<Response> {
		switch (importType) {
			case 'FinancialEntries':
				return await this.api.fetch(`financial-entry-import-items/set-status/${status}`, {
					body: json([itemId]),
					method: 'post'
				});
			case 'MeasureEntries':
				return await this.api.fetch(`measure-entry-import-items/set-status/${status}`, {
					body: json([itemId]),
					method: 'post'
				});
			default:
				throw new Error(`Import type ${importType} is invalid.`);
		}
	}

	async getImport(importId: string): Promise<IImportAM> {
		return await this.api.fetchJson(`imports/${importId}`, { method: 'get' });
	}

	async getImportItemsByStatus(importId: string, status: ImportItemStatus, maxCount: number): Promise<IImportItemAM[]> {
		return await this.api.fetchJson(`imports/${importId}/items/${status}?maxCount=${maxCount}`, { method: 'get' });
	}

	// Cancel import and remove financial automation results
	async cancelImport(importId: string): Promise<Response> {
		return await this.api.fetch(`imports/${importId}/cancel?keepAutomationResults=false`, { method: 'post' });
	}
	// Cancel import only and keep financial automation results
	async cancelImportKeepAutomationResults(importId: string): Promise<Response> {
		return await this.api.fetch(`imports/${importId}/cancel?keepAutomationResults=true`, { method: 'post' });
	}

	async remapImport(importId: string): Promise<Response> {
		return await this.api.fetch(`imports/${importId}/remap`, { method: 'post' });
	}

	async executeImport(importId: string): Promise<Response> {
		return await this.api.fetch(`imports/${importId}/import`, { method: 'post' });
	}

	async getImports(status: 'staged' | 'archived', customerId?: string): Promise<IImportAM[]> {
		if (!customerId) {
			return await this.api.fetchJson(`imports/${status}`);
		} else {
			return await this.api.fetchJson(`customers/${customerId}/imports/${status}`);
		}
	}

	async copyDataToFinancialEntryImport(sourceScopeId: string, targetScopeId: string, sourceFilter: string, keepServiceTargeting: string) {
		return await this.api.fetch(
			`imports/copy-from-scope/${sourceScopeId}/to-scope/${targetScopeId}/financial-entries?sourcefilter=${sourceFilter}&keepServiceTargeting=${keepServiceTargeting}`,
			{ method: 'post' }
		);
	}

	async copyDataToMeasureEntryImport(sourceScopeId: string, targetScopeId: string) {
		return await this.api.fetch(
			`imports/copy-from-scope/${sourceScopeId}/to-scope/${targetScopeId}/measure-entries`,
			{ method: 'post' }
		);
	}
}

export interface IImportPreviewRowAM {
	municipalityCode: string;
	municipalityName: string;
	costCenterIdentifier: string;
	dimensionValues: { [key: string]: string };
}

export interface IFinancialEntryImportPreviewRowAM extends IImportPreviewRowAM {
	accountNumber: string;
	amount: number;
	inputType: string;
}

export interface IMeasureEntryImportPreviewRowAM extends IImportPreviewRowAM {
	measureCode: string;
	value: number;
	serviceCode: string;
}

export interface IImportItemStatusCountAM {
	status: ImportItemStatus;
	count: number;
}

export interface IImportAM {
	id: string;
	type: string;
	customerName: string;
	sourceCustomerName: string;
	status: string;
	scope: IImportScopeAM;
	commonErrors: IImportMappingErrorAM[];
	someEntriesAlreadyExist: boolean;
	statusCounts: { [status: string]: number };
	sourceScope: IImportScopeAM;
	executionState: string | null;
	mode: ImportMode;
	holdReasons: string[];
}

export type ImportMode = 'append' | 'replace' | 'overwrite';

export interface IImportItemAM {
	id: string;
	status: ImportItemStatus;

	costCenterIdentifier?: string;
	sourceServiceId?: string;
	costCenterName?: string;
	costCenterId?: string;

	serviceCode?: string;
	serviceName?: string;
	serviceId?: string;

	municipalityName?: string;

	mappingErrors: { [propertyName: string]: IImportMappingErrorAM[] };
	dimensionValues: IImportItemDimensionValueAM[];
}

export type IFinancialImportItemAM = IImportItemAM & {
	accountNumber?: string;
	accountName?: string;
	sourceAccountId?: string;

	amount: number;
	inputType?: string;
	finalTypeName?: string;
}

export type IMeasureImportItem = IImportItemAM & {
	measureCode: string;
	measureName?: string;

	municipalityCode?: string;

	value: number;
	existingValue?: number;
}

export interface IImportScopeAM {
	id: string;
	name: string;
	isClosed: boolean;
}

export interface IImportMappingErrorAM {
	type: string;
	severity: string;
}

export interface IImportItemDimensionValueAM {
	dimensionName: string;
	valueIdentifier: string;
	accountingDimensionValueId: string;
	mappingErrors: IImportMappingErrorAM[];
}

export type ImportItemStatus = 'Error' | 'Warning' | 'Staged' | 'Rejected' | 'Imported';

export const ImportItemStatus = {
	Error: 'Error' as ImportItemStatus,
	Warning: 'Warning' as ImportItemStatus,
	Staged: 'Staged' as ImportItemStatus,
	Rejected: 'Rejected' as ImportItemStatus,
	Imported: 'Imported' as ImportItemStatus
};

export type ImportMappingErrorSeverity = 'Warning' | 'Error';

export const ImportMappingErrorSeverity = {
	Warning: 'Warning' as ImportMappingErrorSeverity,
	Error: 'Error' as ImportMappingErrorSeverity
};
